'use client';

import Image from 'next/image';
import { useInView } from '@/lib/useInView';

export interface AwardEntry {
  logo: {
    src: string;
    alt: string;
    width?: number;
    height?: number;
  };
  awards: string | string[]; // Can be a single string or array of award strings
}

interface AwardsSectionProps {
  backgroundColor: string;
  awards: AwardEntry[];
}

/**
 * AwardsSection - Displays an awards section at the bottom of case study pages
 * 
 * Full-width section with customizable background color, displaying "Awards" 
 * heading followed by a vertical list of award entries. Each entry shows a 
 * logo on the left and award details on the right, separated by horizontal dividers.
 * 
 * @component
 * @example
 * ```tsx
 * <AwardsSection 
 *   backgroundColor="#D0E5FF"
 *   awards={[
 *     {
 *       logo: {
 *         src: "/images/icons/awards/lions-icon.png",
 *         alt: "Cannes Lions",
 *         width: 150
 *       },
 *       awards: ["1 Gold (Health & Wellness)", "2 Silver (Health & Wellness, Design)", "1 Bronze (Mobile)"]
 *     },
 *     {
 *       logo: {
 *         src: "/images/icons/awards/lia.png",
 *         alt: "LIA Award"
 *       },
 *       awards: "4 Gold Digital"
 *     }
 *   ]}
 * />
 * ```
 */
export default function AwardsSection({ 
  backgroundColor,
  awards 
}: AwardsSectionProps) {
  const { ref } = useInView<HTMLElement>();
  
  const renderAwardText = (award: string | string[]) => {
    if (Array.isArray(award)) {
      return award.map((awardText, index) => (
        <div key={index} className={index > 0 ? 'mt-2' : ''}>
          {awardText}
        </div>
      ));
    }
    return award;
  };

  return (
    <section 
      ref={ref}
      className="animate-in-view w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw]"
      style={{ backgroundColor }}
      aria-label="Awards section"
    >
      <div className="max-w-7xl mx-auto px-[1rem] py-[3.125rem] md:py-[4.5rem] md:px-8">
        {/* Awards Heading */}
        <h2 className="heading-2 border-b border-black pb-[1.875rem]">
          Awards
        </h2>

        {/* Awards List */}
        <div className="mt-3">
          {awards.map((entry, index) => (
            <div key={index}>
              {/* Award Entry */}
              <div className={`flex flex-col mt-3 md:flex-row md:justify-between md:items-start`}>
                {/* Logo - Left (visible on mobile and desktop, aligned to left) */}
                <div className="flex-shrink-0 mb-3 md:mb-0">
                  <div 
                    className="relative"
                    style={{
                      width: entry.logo.width || 150,
                      height: entry.logo.height || 60
                    }}
                  >
                    <Image
                      src={entry.logo.src}
                      alt={entry.logo.alt}
                      width={entry.logo.width || 150}
                      height={entry.logo.height || 60}
                      className="object-contain object-left"
                      style={{ width: '100%', height: '100%' }}
                    />
                  </div>
                </div>

                {/* Award Text - Right (left-aligned on mobile, right-aligned on desktop) */}
                <div className="md:flex-1 md:text-right">
                  <div className="body-default">
                    {renderAwardText(entry.awards)}
                  </div>
                </div>
              </div>

              {/* Separator - Show between entries, not after last */}
              {index < awards.length - 1 && (
                <div 
                  className="h-px"
                  style={{ 
                    backgroundColor: 'rgba(0, 0, 0, 0.2)',
                    marginTop: '2.25rem'
                  }}
                  aria-hidden="true"
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

